import { Box, Chip, Grid, Button, Paper, Typography, Divider, Link } from '@mui/material';
import { useEffect, useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { FormProvider, RHFDateRangePickerField, RHFSelectField, RHFTextField } from "../../../common/hook-form"
import Loader from "../../../common/Loader"
import { formatCurrency, formatDateForApi, parseDateAndTime } from "../../../helpers/UtilityHelper"
import { useLazyGetPaginatedShipmentsQuery } from "../../../redux/commonRTK"
import ProzoDataGrid from "../../../common/ProzoDataGrid";
import _ from "lodash";
import { apiConstants, postGeneric } from '../../../common/constants';
import { useSnackbar } from 'notistack';
import { getMerchant } from '../../../helpers/ReduxHelpers';
import { ManifestCourierFilter } from '../../../common/hook-form/CourierFilter';
import { LoadingButton } from '@mui/lab';
import MUIModal from '../../../common/MUIModal';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { alertStripSx, helpTextSx, helpTextTypeSx, primaryBtnSx, secondaryBtnSx, btnRowSx } from '../../../styles/patterns';
import { ERROR, NAVY, PRIMARY_BLUE, SUCCESS, SUBTLE } from '../../../styles/foundation/colors';
import { primaryTextSx, secondaryTextSx, awbTextSx, cellStackSx, getStatusChipSx, getPaymentChipSx } from './cellStyles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const excludes = "additionalData,orderDate,shipmentType,dispatchMode,invoiceNumber,channelName,orderType,pickupDetails.pickupTime,pickupDetails.from_phone_number,pickupDetails.from_email,pickupDetails.from_country,pickupDetails.from_address,pickupDetails.gstin,pickupDetails.from_state,courierDetail.prozoId,gstInfo,shipmentDetail,deliveryDetails,returnInfo,deliveryTime,itemList,actualDeliveryTat,proshipDeliveryTat,courierDeliveryTat,actualPickUpTime,lastStatusUpdateTime,edd,epd,lifeCycleComplete,courier,merchantDetail,courierId,version,order_history,orderManifest,invoiceUrl,courierPricingRTO,courierPricing,overriddenCourierAccountCode,isDiscrepancyRaised,discrepancyId,securityKey,sortCode,channelType,createdByUser,skipRule,modifiedByUser,rvp_reason,channel_name,customer_detail,cpErrorList,mongoLabelSignedUrlDetails,overriddenCourierRule,mongoInvoiceSignedUrlDetails,merchant,merchantPricing,courierPricingRTO,modifiedByUser,lastModifiedDate,terminalStatus,merchantPricingRTO,customerDetail";

// Server-side cap on /manifest/generateGrouped — mirror it here so the user finds out
// before the round-trip, not after.
const MAX_AWBS = 1000;

// Grouping key must match the backend exactly: courier PARENT (raw string) x pickup pincode.
// One manifest is generated per group.
const groupKeyOf = (row) => `${row?.courierDetail?.parent || ''}|${row?.pickup_details?.from_pincode || ''}`;

// Grouped variant of Pending Pickups — piloted on /pending-pickups only, while
// /labels-invoices?tab=manifest keeps the original single-group PendingPickups.
// Selection is UNRESTRICTED: the backend buckets the batch by (courier parent x
// pickup pincode) and returns one manifest per bucket in a single call.
function PendingPickupsGrouped() {
    const merchant = getMerchant();
    const merchantId = merchant.id ? merchant.id : ''

    const [reload, setReload] = useState(0)
    const { enqueueSnackbar } = useSnackbar()
    const [page, setPage] = useState(0);
    const [isGenerating, setIsGenerating] = useState(false);
    const [sizePerPage, setSizePerPage] = useState(10);
    const [andFilters, setAndFilters] = useState([]);
    const [rowSelectionModel, setRowSelectionModel] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [combinedData, setCombinedData] = useState([]);
    const [result, setResult] = useState(null);
    const [getPaginatedShipments, { data, isLoading, isFetching }] = useLazyGetPaginatedShipmentsQuery()

    const defaultFilters = [
        { field: "orderStatus", operator: "in", value: ['ORDER_PLACED', 'PICKUP_PENDING', 'PICKUP_FAILED', 'OUT_FOR_PICKUP', 'AWB_REGISTERED'] },
        { field: "manifestDetails", operator: "exists", value: false },
        // Orders without an AWB can never be manifested — keep them out of the grid.
        { field: "awbNumber", operator: "exists", value: true },
        { field: "shipmentType", operator: "eq", value: "B2C" },
        { field: "merchant.$id", operator: "eq", value: merchantId }]

    // Channel filter carried over from PendingPickups — shown for the same merchant
    // whose ops workflow manifests channel-by-channel.
    const beardoChannels = [
        { "id": "SHOPIFY", "parent": "SHOPIFY" },
        { "id": "WMS", "parent": "WMS" },
        { "id": "Proship", "parent": "Proship" },
    ]

    useEffect(() => {
        getPaginatedShipments({
            offset: (page * sizePerPage),
            limit: sizePerPage,
            andfilter: [...defaultFilters, ...andFilters],
            excludes
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, sizePerPage, andFilters, reload])

    // Selection survives server-side paging, so keep every row we have ever seen —
    // selected ids are resolved against this pool, not just the current page.
    useEffect(() => {
        if (data?.data) {
            setCombinedData(prevData => {
                const newData = data.data.filter(newItem =>
                    !prevData.some(prevItem => prevItem.id === newItem.id)
                );
                return [...prevData, ...newData];
            });
        }
    }, [data?.data]);

    useEffect(() => {
        setSelectedRows(combinedData.filter(obj => rowSelectionModel.includes(obj.id)))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(rowSelectionModel), combinedData]);

    // Live grouping preview — selection is unrestricted; the server splits it into one
    // manifest per (courier parent x pickup pincode). Show the user what they'll get.
    const grouping = useMemo(() => {
        const buckets = _.groupBy(selectedRows, groupKeyOf);
        const bucketList = Object.keys(buckets).map((key) => {
            const rows = buckets[key];
            return {
                key,
                courier: rows[0]?.courierDetail?.parent || 'Unknown',
                pincode: rows[0]?.pickup_details?.from_pincode || '—',
                pickupName: rows[0]?.pickup_details?.from_name || '',
                count: rows.length,
            };
        });
        return {
            buckets: bucketList,
            couriers: _.uniq(bucketList.map(b => b.courier)).length,
            pickups: _.uniq(bucketList.map(b => b.pincode)).length,
        };
    }, [selectedRows]);

    const overCap = selectedRows.length > MAX_AWBS;
    const canGenerate = selectedRows.length > 0 && !overCap;

    const methods = useForm({
        defaultValues: { courier: null, pickup_pincode: '', awb: '', channel: '', fromDate: null, toDate: null }
    });
    const { handleSubmit, reset } = methods;

    const applyFilters = (filters) => {
        let appliedFilters = [];
        if (filters.courier) {
            appliedFilters.push({ "field": "courierDetail.key", "operator": "eq", "value": filters.courier });
        }
        if (filters.pickup_pincode) {
            appliedFilters.push({ "field": "pickupDetails.from_pincode", "operator": "eq", "value": filters.pickup_pincode })
        }
        if (filters.fromDate) {
            appliedFilters.push({ "field": "awbRegisteredDate", "operator": "gte", "value": formatDateForApi(filters.fromDate) })
        }
        if (filters.toDate) {
            appliedFilters.push({ "field": "awbRegisteredDate", "operator": "lte", "value": formatDateForApi(filters.toDate) })
        }
        if (filters.awb) {
            appliedFilters.push({ "field": "awbNumber", "operator": "eq", "value": filters.awb })
        }
        if (filters.channel) {
            appliedFilters.push({ "field": "channelName", "operator": "eq", "value": filters.channel })
        }
        setAndFilters(appliedFilters)
    }

    const columns = [
        {
            field: 'actions', headerAlign: "left", hideSortIcons: true, headerName: 'Order Details', flex: 1, minWidth: 140,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <Box sx={cellStackSx}>
                        <Typography sx={primaryTextSx}>{row.reference}</Typography>
                        {row.shippingLabelId && row.reference !== row.shippingLabelId && (
                            <Typography sx={secondaryTextSx}>{row.shippingLabelId}</Typography>
                        )}
                        <Typography sx={secondaryTextSx}>
                            {row.createdDate ? parseDateAndTime(row.createdDate) : ""}
                        </Typography>
                    </Box>
                )
            },
        },
        {
            field: 'awb', headerAlign: "left", hideSortIcons: true, headerName: 'Courier Partner', flex: 1, minWidth: 150,
            renderCell: (params) => {
                const { row } = params;
                const courierParent = row.courierDetail?.parent;
                const childName = row.courierDetail?.name;
                return (
                    <Box sx={cellStackSx}>
                        <Typography sx={awbTextSx}>{row?.awb_number}</Typography>
                        <Typography sx={primaryTextSx}>{courierParent}</Typography>
                        {childName && childName !== courierParent && (
                            <Typography sx={secondaryTextSx}>{childName}</Typography>
                        )}
                    </Box>
                )
            },
        },
        {
            field: 'pickup', headerAlign: "left", hideSortIcons: true, headerName: 'Pickup Details', flex: 1.2, minWidth: 180,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <Box sx={cellStackSx}>
                        <Typography sx={primaryTextSx}>{row.pickup_details?.from_name}</Typography>
                        <Typography sx={secondaryTextSx}>
                            {row.pickup_details?.from_city}, {row.pickup_details?.from_pincode}
                        </Typography>
                    </Box>
                )
            },
        },
        {
            field: 'paymentDetails', headerAlign: "left", hideSortIcons: true, headerName: 'Payment', flex: 0.8, minWidth: 110,
            renderCell: (params) => {
                const { row } = params;
                const paymentMode = row.payment_mode || '';
                return (
                    <Box sx={cellStackSx}>
                        <Typography sx={primaryTextSx}>
                            {row.invoice_value ? formatCurrency(row.invoice_value) : "N/A"}
                        </Typography>
                        {paymentMode && <Chip size="small" variant="outlined" label={paymentMode} sx={getPaymentChipSx(paymentMode)} />}
                    </Box>
                )
            },
        },
        {
            field: 'statusDetails', headerAlign: "left", hideSortIcons: true, headerName: 'Status', flex: 0.8, minWidth: 120,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <Box sx={cellStackSx}>
                        <Chip size="small" variant="outlined" label={row.orderStatus} sx={getStatusChipSx(row.orderStatus)} />
                    </Box>
                )
            },
        },
    ];

    const generateGrouped = () => {
        if (!canGenerate) {
            enqueueSnackbar("Select at least one shipment to proceed.", { variant: "warning" });
            return;
        }
        setIsGenerating(true)
        postGeneric(apiConstants.GENERATE_GROUPED_MANIFEST, {
            awb_numbers: selectedRows.map(obj => obj.awb_number)
        }).then((res) => {
            if (res.status === 200) {
                setResult(res.data)
                const generated = res.data?.manifests?.length || 0;
                if (generated > 0) {
                    enqueueSnackbar(`${generated} manifest${generated > 1 ? 's' : ''} generated`, { variant: "success" });
                }
                setSelectedRows([])
                setRowSelectionModel([])
                setCombinedData([])
                setReload(reload + 1)
            }
        }).catch((err) => {
            console.log(err)
            let message = err?.response?.data?.message ? err?.response?.data?.message : "Something went wrong while generating manifests"
            enqueueSnackbar(message, { variant: "error" })
        }).finally(() => setIsGenerating(false))
    }

    // Must run synchronously inside the click's user-activation stack — window.open from a
    // setTimeout is treated as non-user-initiated and popup-blocked silently. Browsers may
    // still allow only one popup per click, so warn when any open was blocked instead of
    // letting the user believe every PDF opened.
    const downloadAll = () => {
        let blocked = 0;
        (result?.manifests || []).forEach((m) => {
            if (m.manifestUrl) {
                const w = window.open(m.manifestUrl, '_blank');
                if (!w) blocked += 1;
            }
        });
        if (blocked > 0) {
            enqueueSnackbar(
                `${blocked} PDF tab${blocked > 1 ? 's were' : ' was'} blocked by the browser's pop-up blocker — allow pop-ups for this site, or use the View PDF links.`,
                { variant: "warning" }
            );
        }
    }

    return (
        <>
            {(isLoading || isFetching) && <Loader />}

            {/* Result modal — one row per generated manifest, plus failures/rejections */}
            <MUIModal open={Boolean(result)} handleClose={() => setResult(null)} maxWidth={'md'}
                title="Manifest Generation Result">
                {(result?.manifests?.length || 0) > 0 && (
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.semibold, color: NAVY }}>
                                Generated Manifests ({result.manifests.length})
                            </Typography>
                            <Button size="small" variant="outlined" sx={secondaryBtnSx} onClick={downloadAll}>Download All</Button>
                        </Box>
                        {result.manifests.map((m) => (
                            <Box key={m.manifestNumber} sx={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                border: '1px solid #e0e0e0', borderRadius: '8px', px: 1.5, py: 1, mb: 1,
                            }}>
                                <Box>
                                    <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: NAVY }}>
                                        {m.courier} — {m.pickupName ? `${m.pickupName}, ` : ''}{m.pickupPincode}
                                    </Typography>
                                    <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE }}>
                                        Manifest {m.manifestNumber} · {m.totalOrders} shipment{m.totalOrders > 1 ? 's' : ''}
                                    </Typography>
                                </Box>
                                <Link href={m.manifestUrl} target="_blank" sx={{
                                    fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold,
                                    color: PRIMARY_BLUE, display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap',
                                }}>
                                    View PDF <OpenInNewIcon sx={{ fontSize: 14 }} />
                                </Link>
                            </Box>
                        ))}
                    </>
                )}
                {(result?.failedBuckets?.length || 0) > 0 && (
                    <>
                        <Divider sx={{ my: 1.5 }} />
                        <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.semibold, color: ERROR, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ErrorOutlineIcon sx={{ fontSize: 16 }} /> Failed Groups ({result.failedBuckets.length}) — these shipments are still manifestable, retry after fixing
                        </Typography>
                        {result.failedBuckets.map((b, i) => (
                            <Box key={i} sx={alertStripSx(ERROR)}>
                                <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'] }}>
                                    {b.courier} — {b.pickupPincode} ({b.awbCount} AWBs): {b.reason}
                                </Typography>
                            </Box>
                        ))}
                    </>
                )}
                {(result?.rejectedAwbs?.length || 0) > 0 && (
                    <>
                        <Divider sx={{ my: 1.5 }} />
                        <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.semibold, color: NAVY, mb: 1 }}>
                            Skipped AWBs ({result.rejectedAwbs.length})
                        </Typography>
                        {result.rejectedAwbs.map((r) => (
                            <Typography key={r.awbNumber} sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE }}>
                                {r.awbNumber} — {r.reason}
                            </Typography>
                        ))}
                    </>
                )}
            </MUIModal>

            <Grid container spacing={2}>
                <Grid item xs={12} spacing={2}>
                    <Paper sx={{ p: 2, boxShadow: 1, borderRadius: 0 }}>
                        <FormProvider methods={methods} onSubmit={handleSubmit(applyFilters)}>
                            <Grid container spacing={2} alignItems="center">
                                {merchantId === '65f83d1b9e30dd05590fca9f' && (
                                    <Grid item xs={2}>
                                        <RHFSelectField width="100%" name="channel" label={`Select Channel`} options={beardoChannels.map((val) => ({
                                            value: val.id,
                                            label: val.parent
                                        }))} />
                                    </Grid>
                                )}
                                <Grid item xs={3} sx={{ overflow: 'hidden' }}>
                                    <ManifestCourierFilter />
                                </Grid>
                                <Grid item xs={3}>
                                    <RHFTextField name="pickup_pincode" label="Pickup Pincode" />
                                </Grid>
                                <Grid item xs={3}>
                                    <RHFTextField name="awb" label="AWB Number" color="secondary" />
                                </Grid>
                                <Grid item xs={3}>
                                    <RHFDateRangePickerField label="Created Date" maxDate={new Date()} />
                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={btnRowSx}>
                                        <Button type="submit" variant="contained" size="small" sx={primaryBtnSx}>Filter</Button>
                                        <Button variant="outlined" size="small" sx={secondaryBtnSx} onClick={() => reset()}>Reset</Button>
                                        <LoadingButton variant="contained" size="small" sx={primaryBtnSx} loading={isGenerating}
                                            disabled={!canGenerate} onClick={generateGrouped}>
                                            Bulk Manifest{grouping.buckets.length > 1 ? ` (${grouping.buckets.length})` : ''}
                                        </LoadingButton>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    {overCap ? (
                                        <Box sx={alertStripSx(ERROR)}>
                                            <WarningAmberIcon sx={{ fontSize: 16, color: ERROR }} />
                                            <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'] }}>
                                                Maximum {MAX_AWBS.toLocaleString()} shipments per generation — you have selected {selectedRows.length.toLocaleString()}. Deselect some shipments or generate in batches.
                                            </Typography>
                                        </Box>
                                    ) : selectedRows.length > 0 ? (
                                        <Box sx={helpTextSx}>
                                            <CheckCircleOutlineIcon sx={{ fontSize: 16, color: SUCCESS, mt: 0.2 }} />
                                            <Box>
                                                <Typography sx={helpTextTypeSx}>
                                                    {selectedRows.length} shipment{selectedRows.length > 1 ? 's' : ''} selected → <b>{grouping.buckets.length} manifest{grouping.buckets.length > 1 ? 's' : ''}</b> across {grouping.couriers} courier{grouping.couriers > 1 ? 's' : ''} and {grouping.pickups} pickup location{grouping.pickups > 1 ? 's' : ''}
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                                    {grouping.buckets.map((b) => (
                                                        <Chip key={b.key} size="small" variant="outlined"
                                                            label={`${b.courier} × ${b.pincode} (${b.count})`}
                                                            sx={{ fontFamily: FONT, fontSize: SIZE['2xs'] }} />
                                                    ))}
                                                </Box>
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Box sx={helpTextSx}>
                                            <InfoOutlinedIcon sx={{ fontSize: 16, color: PRIMARY_BLUE, mt: 0.2 }} />
                                            <Typography sx={helpTextTypeSx}>
                                                Select any shipments — mixing couriers and pickup locations is fine. One manifest is generated per courier &amp; pickup location automatically.
                                            </Typography>
                                        </Box>
                                    )}
                                </Grid>
                            </Grid>
                        </FormProvider>
                    </Paper>
                </Grid>
                <Grid item xs={12}>
                    <ProzoDataGrid
                        columns={columns}
                        hideFooterPagination={false}
                        autoHeight={true}
                        rows={data?.data || []}
                        checkboxSelection={true}
                        keepNonExistentRowsSelected={true}
                        sizePerPage={sizePerPage}
                        setSizePerPage={setSizePerPage}
                        setPage={setPage}
                        page={page}
                        rowSelectionModel={rowSelectionModel}
                        setRowSelectionModel={setRowSelectionModel}
                        pagination={true}
                        rowCount={data?.count || 0}
                        filterMode={"server"}
                        getRowId={(row) => row.id} />
                </Grid>
            </Grid>
        </>
    )
}
export default PendingPickupsGrouped
